import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';

/**
 * AnimatedTextCarousel Component
 * 
 * Cycles through a list of texts with smooth fade transitions.
 * Styled to match the Iris logo color scheme.
 */
interface AnimatedTextCarouselProps {
  texts: string[];
  interval?: number; // Time in milliseconds between transitions
  fontSize?: string | { xs?: string; md?: string };
  fontWeight?: number;
  color?: string;
}

const AnimatedTextCarousel: React.FC<AnimatedTextCarouselProps> = ({
  texts,
  interval = 2500,
  fontSize = { xs: '1.5rem', md: '2rem' },
  fontWeight = 600,
  color = '#007AFF',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const cycleInterval = setInterval(() => {
      setIsVisible(false);
      
      setTimeout(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % texts.length);
        setIsVisible(true);
      }, 300); // Half of transition duration for smooth fade
    }, interval);

    return () => clearInterval(cycleInterval);
  }, [texts.length, interval]);

  return (
    <Box
      sx={{
        minHeight: { xs: '60px', md: '80px' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontSize,
          fontWeight,
          color,
          textAlign: 'center',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
          transition: 'opacity 0.3s ease, transform 0.3s ease',
          position: 'absolute',
          width: '100%',
        }}
      >
        {texts[currentIndex]}
      </Typography>
    </Box>
  );
};

export default AnimatedTextCarousel;

